import { GetServerSideProps } from 'next';
import { useContext, useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import styled from 'styled-components';

import { MagicBackground } from '../../../../../assets/icons/MagicBackground';
import theme from '../../../../../assets/theme';
import { Button } from '../../../../../components/_atoms/Button';
import MaxSizeAspectRatioContainer from '../../../../../components/_molecules/MaxSizeAspectRatioContainer';
import { MaxSizeAspectRatioContainerContext } from '../../../../../components/_molecules/MaxSizeAspectRatioContainer/MaxSizeAspectRatioContainer';
import useDeleteGame from '../../../../../hooks/useDeleteGame';
import useRouterMatch from '../../../../../hooks/useRouterMatch';
import { useHideIntercom } from '../../../../../providers/CustomIntercomStateProvider';
import NoAccess from '../../../../../providers/NoAccess';
import { getBaseUrl } from '../../../../../utils/client';
import { detectAndBestEffortSetLanguage } from '../../../../../utils/localisationUtils';
import { configureStore } from '../../../../../utils/store';

const ScaleComponent = ({ hostUrl, scale }: { hostUrl: string; scale: number }) => {
  const { height = 0, width = 0, scale: existingScale = 0 } = useContext(
    MaxSizeAspectRatioContainerContext,
  );
  const [showIframe, setShowIframe] = useState(false);

  useEffect(() => {
    setTimeout(() => setShowIframe(true), 1000);
  }, []);

  const containerWidth = width / scale / existingScale;
  const containerHeight = height / scale / existingScale;
  return (
    <StaticContainer
      $width={containerWidth}
      $height={containerHeight}
      $scale={`scale(${scale})`}
    >
      <DeviceContainer>
        {showIframe && (
          <iframe
            src={hostUrl}
            title="View"
            frameBorder="0"
            style={{
              width: '100%',
              height: '100%',
            }}
          />
        )}
      </DeviceContainer>
    </StaticContainer>
  );
};

const StaticContainer = styled.div<{
  $scale: string;
  $height: number;
  $width: number;
}>`
  height: ${({ $height }) => $height}px;
  width: ${({ $width }) => $width}px;
  transform: ${({ $scale }) => $scale};
  transform-origin: top left;
  display: inline-flex;
`;

const PreviewGamePage = () => {
  const { tenantId, gameId: previewGameId, router } = useRouterMatch();
  const { deleteGame } = useDeleteGame();
  const previewGameIdRef = useRef<string>();

  const screenRef = useRef<HTMLDivElement>(null);
  useHideIntercom();

  const [isVertical, setIsVertical] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      const height = screenRef.current?.clientHeight || 0;
      const width = screenRef.current?.clientWidth || 0;

      if (height > width) {
        setIsVertical(true);
      } else {
        setIsVertical(false);
      }
    });

    ro.observe(screenRef.current as HTMLElement);

    return () => {
      ro.disconnect();
    };
  }, []);

  const [hostUrl, setHostUrl] = useState<string>(
    `${getBaseUrl()}/${tenantId}/games/${previewGameId}/live?isPreview=true`,
  );
  useEffect(() => {
    setHostUrl(`${getBaseUrl()}/${tenantId}/games/${previewGameId}/live?isPreview=true`);
    previewGameIdRef.current = previewGameId;
  }, [tenantId, previewGameId]);

  const exitPreview = () => {
    router.back();
  };

  useEffect(() => {
    return () => {
      if (previewGameIdRef.current) {
        deleteGame({ config: { gameId: previewGameIdRef.current } });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContainer ref={screenRef}>
      <BackgroundContainer>
        <MagicBackground />
      </BackgroundContainer>
      {isVertical == false && (
        <DesktopContainer>
          <BackButtonContainer>
            <BackButton
              type="primary"
              styles={{
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                height: 42,
              }}
              onClick={() => {
                exitPreview();
              }}
            >
              Exit preview
            </BackButton>
          </BackButtonContainer>

          <ChromebookContainer>
            <div style={{ width: '100%', padding: '20px 0px 5px 0px' }}>
              <h2
                style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: 36,
                  marginBottom: 0,
                }}
              >
                Student screen
              </h2>
            </div>
            <MaxSizeAspectRatioContainer aspect={{ width: 800, height: 600 }}>
              <ScaleComponent
                scale={0.7}
                hostUrl={`https://${configureStore().config.game.url}`}
              ></ScaleComponent>
            </MaxSizeAspectRatioContainer>
          </ChromebookContainer>
          <ChromebookContainer>
            <div style={{ width: '100%', padding: '20px 0px 5px 0px' }}>
              <h2
                style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: 36,
                  marginBottom: 0,
                }}
              >
                Classroom screen
              </h2>
            </div>
            <MaxSizeAspectRatioContainer aspect={{ width: 800, height: 600 }}>
              <ScaleComponent scale={0.7} hostUrl={hostUrl}></ScaleComponent>
            </MaxSizeAspectRatioContainer>
          </ChromebookContainer>
        </DesktopContainer>
      )}
      {isVertical === true && (
        <MobileContainer>
          <BackButtonContainer>
            <BackButton
              type="primary"
              styles={{
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                height: 42,
              }}
              onClick={() => {
                exitPreview();
              }}
            >
              Exit preview
            </BackButton>
          </BackButtonContainer>

          <MobileChromebookContainer>
            <div style={{ width: '100%', padding: '20px 0px 5px 0px' }}>
              <h2
                style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: 36,
                  marginBottom: 0,
                }}
              >
                Student screen
              </h2>
            </div>
            <MaxSizeAspectRatioContainer aspect={{ width: 1920, height: 1080 }}>
              <ScaleComponent
                scale={1.5}
                hostUrl={`https://${configureStore().config.game.url}`}
              ></ScaleComponent>
            </MaxSizeAspectRatioContainer>
          </MobileChromebookContainer>
          <MobileChromebookContainer>
            <div style={{ width: '100%', padding: '20px 0px 5px 0px' }}>
              <h2
                style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: 36,
                  marginBottom: 0,
                }}
              >
                Classroom screen
              </h2>
            </div>
            <MaxSizeAspectRatioContainer aspect={{ width: 1920, height: 1080 }}>
              <ScaleComponent scale={1.5} hostUrl={hostUrl}></ScaleComponent>
            </MaxSizeAspectRatioContainer>
          </MobileChromebookContainer>
        </MobileContainer>
      )}
    </PageContainer>
  );
};

const DeviceContainer = styled.div`
  height: 100%;
  width: 100%;
  border-style: solid;
  border-color: black;
  border-radius: 10px;
  background-color: black;
  border-width: 30px 15px 30px;
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.35);
`;

const BackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  svg {
    height: 98vh; // 99 and 100 adds a scroll...
    width: 100vw;
  }
`;

const PageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: 0px 20px 20px 20px;
  background: radial-gradient(
    145.25% 138.82% at 27.25% -63.82%,
    #ffc698 0%,
    #fff1e6 100%
  );
  overflow: hidden;
`;

const BackButtonContainer = styled.div`
  position: absolute;
  top: 0px;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
  z-index: 2;
`;

const BackButton = styled(Button)`
  padding: 10px 20px;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.effects.magicFloatingShadow};
  color: ${theme.colors.black};
  font-size: 20px;

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.greyLight};
    color: ${theme.colors.black};
    box-shadow: ${theme.effects.defaultShadow};
  }
`;

const ChromebookContainer = styled.div`
  width: 100%;
  height: 100%;
  z-index: 1;
  display: grid;
  grid-template-rows: 70px auto; // 70px for the header and fill the rest for the screens
`;

const MobileChromebookContainer = styled.div`
  width: 100%;
  height: 100%;
  z-index: 1;
  display: grid;
  grid-template-rows: 70px calc(100% - 70px); // 70px for the header and fill the rest for the screens
`;

const DesktopContainer = styled.div`
  display: grid;
  height: 100%;
  width: 100%;
  grid-template-columns: 49% 49%;
  gap: 20px;
`;

const MobileContainer = styled.div`
  display: grid;
  height: 100%;
  width: 100%;
  grid-template-rows: 49% 49%;
  padding-top: 20px;
  gap: 20px;
`;

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  return {
    props: {
      ...(await detectAndBestEffortSetLanguage(context)),
    },
  };
};

const PreviewGamePageExport = () => {
  return (
    <NoAccess>
      <PreviewGamePage />
    </NoAccess>
  );
};
export default PreviewGamePageExport;
